import { useEffect, useState } from "react";
import {
  fetchEnumValues,
  type Condition,
  type ConditionGroup,
  type FilterableFieldDto,
  type FilterOperator,
  type SavedFilterDto,
} from "../api";
import { OPERATORS_BY_TYPE } from "../fields";

function fieldByKey(fields: FilterableFieldDto[], key: string): FilterableFieldDto | undefined {
  return fields.find((f) => f.field_key === key);
}

function formatValue(v: Condition["value"]): string {
  return Array.isArray(v) ? v.join(", ") : String(v);
}

function AddConditionForm({
  fields,
  token,
  asOf,
  onAdd,
}: {
  fields: FilterableFieldDto[];
  token: string;
  asOf: string;
  onAdd: (c: Condition) => void;
}) {
  const [fieldKey, setFieldKey] = useState("");
  const [operator, setOperator] = useState<FilterOperator>("gt");
  const [value, setValue] = useState("");
  const [enumOptions, setEnumOptions] = useState<string[] | null>(null);
  const [enumLoading, setEnumLoading] = useState(false);

  // `fields` arrives asynchronously (GET /filterable-fields resolves after
  // this component's first mount) -- useState's initial value only applies
  // ONCE, at that first (still-empty) render, so seeding fieldKey from
  // fields[0] there would leave it permanently stuck on "". Sync instead:
  // whenever the registry updates and the current selection isn't a real
  // field in it, snap to the first filterable one.
  useEffect(() => {
    if (fields.length === 0) return;
    if (fields.some((f) => f.field_key === fieldKey)) return;
    const first = fields.find((f) => f.filterable);
    if (first) {
      setFieldKey(first.field_key);
      if (first.data_type) setOperator(OPERATORS_BY_TYPE[first.data_type][0].value as FilterOperator);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fields]);

  const field = fieldByKey(fields, fieldKey);
  const ops = field?.data_type ? OPERATORS_BY_TYPE[field.data_type] : [];

  useEffect(() => {
    if (field?.data_type !== "enum") {
      setEnumOptions(null);
      return;
    }
    let cancelled = false;
    setEnumLoading(true);
    fetchEnumValues(token, field.field_key, asOf)
      .then((vals) => {
        if (!cancelled) setEnumOptions(vals);
      })
      .catch(() => {
        if (!cancelled) setEnumOptions([]);
      })
      .finally(() => {
        if (!cancelled) setEnumLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [field?.field_key, field?.data_type, token, asOf]);

  function submit() {
    if (!field || !value.trim()) return;
    const raw =
      operator === "in"
        ? value.split(",").map((v) => v.trim())
        : field.data_type === "number" || field.data_type === "percent"
          ? Number(value)
          : value.trim();
    onAdd({ field: fieldKey, operator, value: raw as Condition["value"] });
    setValue("");
  }

  if (!field) {
    return <span className="cond-add-form mut">{fields.length === 0 ? "loading fields…" : "no filterable fields"}</span>;
  }

  return (
    <span className="cond-add-form">
      <select
        value={fieldKey}
        onChange={(e) => {
          const next = fieldByKey(fields, e.target.value);
          setFieldKey(e.target.value);
          setValue("");
          if (next?.data_type) setOperator(OPERATORS_BY_TYPE[next.data_type][0].value as FilterOperator);
        }}
      >
        {fields.map((f) => (
          <option key={f.field_key} value={f.field_key} disabled={!f.filterable}>
            {f.label}
            {!f.filterable ? " (display-only)" : ""}
          </option>
        ))}
      </select>
      <select value={operator} onChange={(e) => setOperator(e.target.value as FilterOperator)}>
        {ops.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
      {field.data_type === "enum" ? (
        <select value={value} onChange={(e) => setValue(e.target.value)} disabled={enumLoading}>
          <option value="">{enumLoading ? "loading…" : "choose…"}</option>
          {(enumOptions ?? []).map((v) => (
            <option key={v} value={v}>
              {v}
            </option>
          ))}
        </select>
      ) : (
        <input
          placeholder="value"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && submit()}
        />
      )}
      <button onClick={submit}>+ add</button>
    </span>
  );
}

function GroupPanel({
  group,
  fields,
  token,
  asOf,
  onToggleOp,
  onRemoveCondition,
  onAddCondition,
}: {
  group: ConditionGroup;
  fields: FilterableFieldDto[];
  token: string;
  asOf: string;
  onToggleOp: () => void;
  onRemoveCondition: (idx: number) => void;
  onAddCondition: (c: Condition) => void;
}) {
  return (
    <div className="cond-group">
      <div className="cond-group-op">
        <button onClick={onToggleOp} title="Toggle this group's internal join">
          {group.op}
        </button>
      </div>
      <div className="conds">
        {group.conditions.map((c, idx) => {
          const field = fieldByKey(fields, c.field);
          return (
            <span className="cond-chip" key={`${c.field}-${idx}`}>
              {field?.kind && <span className={`kind-tag ${field.kind}`}>{field.kind}</span>}
              <span className="fld">{field?.label ?? c.field}</span>
              <span className="op">{c.operator}</span>
              <span className="val">{formatValue(c.value)}</span>
              <button className="x" onClick={() => onRemoveCondition(idx)} aria-label="remove condition">
                ×
              </button>
            </span>
          );
        })}
        <AddConditionForm fields={fields} token={token} asOf={asOf} onAdd={onAddCondition} />
      </div>
    </div>
  );
}

function SaveStrategyForm({ onSave, onCancel }: { onSave: (name: string) => void; onCancel: () => void }) {
  const [name, setName] = useState("");
  return (
    <span className="cond-add-form">
      <input
        placeholder="Strategy name…"
        value={name}
        onChange={(e) => setName(e.target.value)}
        onKeyDown={(e) => e.key === "Enter" && name.trim() && onSave(name.trim())}
        autoFocus
      />
      <button onClick={() => name.trim() && onSave(name.trim())}>Save</button>
      <button onClick={onCancel}>Cancel</button>
    </span>
  );
}

export function FilterBuilder({
  groups,
  groupOp,
  fields,
  token,
  asOf,
  savedFilters,
  onGroupsChange,
  onGroupOpChange,
  onAddGroup,
  onRun,
  onClear,
  onSaveStrategy,
  onLoadStrategy,
  onDeleteStrategy,
  onUpdateStrategy,
  loadedFilter,
  running,
}: {
  groups: ConditionGroup[];
  groupOp: "AND" | "OR";
  fields: FilterableFieldDto[];
  token: string;
  asOf: string;
  savedFilters: SavedFilterDto[];
  onGroupsChange: (groups: ConditionGroup[]) => void;
  onGroupOpChange: (op: "AND" | "OR") => void;
  onAddGroup: () => void;
  onRun: () => void;
  onClear: () => void;
  onSaveStrategy: (name: string) => void;
  onLoadStrategy: (f: SavedFilterDto) => void;
  onDeleteStrategy: (filterId: string) => void;
  /** In-place edit of the currently-loaded strategy (same filter_id) --
   * distinct from onSaveStrategy, which always creates a new one. Optional:
   * callers without edit-in-place support (e.g. Indices) simply omit it. */
  onUpdateStrategy?: () => void;
  loadedFilter?: SavedFilterDto | null;
  running: boolean;
}) {
  const [loadOpen, setLoadOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  function updateGroup(i: number, next: ConditionGroup) {
    const copy = groups.slice();
    copy[i] = next;
    onGroupsChange(copy);
  }

  return (
    <section className="filter-panel">
      <div className="filter-head">
        <span className="lbl">Filter · two-level boolean</span>
        <div className="fh-right" style={{ display: "flex", gap: 8, alignItems: "center", position: "relative" }}>
          <button className="btn ghost sm" onClick={() => setLoadOpen((o) => !o)}>
            ▾ Load strategy ({savedFilters.length})
          </button>
          {loadOpen && (
            <div className="columns-popover" style={{ top: 34 }}>
              {savedFilters.length === 0 && <div className="cp-note">No saved strategies yet.</div>}
              {savedFilters.map((f) => (
                <div className="col-row" key={f.filter_id} style={{ justifyContent: "space-between" }}>
                  <span
                    style={{ cursor: "pointer" }}
                    onClick={() => {
                      onLoadStrategy(f);
                      setLoadOpen(false);
                    }}
                  >
                    {f.name}
                  </span>
                  <button className="x" onClick={() => onDeleteStrategy(f.filter_id)} aria-label="delete strategy">
                    ×
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      <div className="filter-builder">
        {groups.map((g, i) => (
          <div key={i}>
            {i > 0 && (
              <div className="join-row">
                <button className="join-pill" onClick={() => onGroupOpChange(groupOp === "AND" ? "OR" : "AND")}>
                  {groupOp}
                </button>
                <span className="join-line" />
              </div>
            )}
            <GroupPanel
              group={g}
              fields={fields}
              token={token}
              asOf={asOf}
              onToggleOp={() => updateGroup(i, { ...g, op: g.op === "AND" ? "OR" : "AND" })}
              onRemoveCondition={(idx) =>
                updateGroup(i, { ...g, conditions: g.conditions.filter((_, j) => j !== idx) })
              }
              onAddCondition={(c) => updateGroup(i, { ...g, conditions: [...g.conditions, c] })}
            />
          </div>
        ))}
        <div className="filter-actions">
          <button className="btn primary" onClick={onRun} disabled={running}>
            {running ? "Running…" : "▷ Run screen"}
          </button>
          <button className="btn ghost" onClick={onClear}>
            Clear
          </button>
          {saving ? (
            <SaveStrategyForm
              onSave={(name) => {
                onSaveStrategy(name);
                setSaving(false);
              }}
              onCancel={() => setSaving(false)}
            />
          ) : (
            <>
              {loadedFilter && (
                <button
                  className="btn"
                  onClick={onUpdateStrategy}
                  title={`Save these changes back to "${loadedFilter.name}" -- same strategy, not a new one`}
                >
                  Update "{loadedFilter.name}"
                </button>
              )}
              <button className="btn ghost" onClick={() => setSaving(true)}>
                Save as new strategy
              </button>
            </>
          )}
          <button className="btn" style={{ marginLeft: "auto" }} onClick={onAddGroup}>
            + Add group
          </button>
        </div>
      </div>
    </section>
  );
}
